import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';

import { remoteConfigAtom } from 'src/stores/auth/atoms';
import { matchInfoAtom, peerCameraEnabledAtom } from 'src/stores/match/atoms';
import {
  encodeCanvasToPng,
  isCanvasBlobEncodeDroppedError,
} from 'src/utils/canvas/canvasBlobEncoder';
import { isObjectKeyTrue, toFiniteNumber } from 'src/utils/common';
import { uploadContentManager } from 'src/utils/content-manager';
import { mark } from 'src/utils/perf';

import { uploadServerScreeningContents } from './apis';
import { Screen, ScreeningContentType, ScreeningTarget } from './types';

export const DEFAULT_MODERATION_CAPTURE_INTERVAL_MS = 2000;

// --- Capture stats (누적, 비영속) ---

export interface ServerScreeningStats {
  status: 'idle' | 'capturing';
  capturedCount: number;
  uploadedCount: number;
  errorCount: number;
  skippedCount: number;
  lastCaptureSize: number;
  lastCaptureTimeMs: number;
}

const DEFAULT_SERVER_SCREENING_STATS: ServerScreeningStats = {
  status: 'idle',
  capturedCount: 0,
  uploadedCount: 0,
  errorCount: 0,
  skippedCount: 0,
  lastCaptureSize: 0,
  lastCaptureTimeMs: 0,
};

export const serverScreeningStatsAtom = atom<ServerScreeningStats>(DEFAULT_SERVER_SCREENING_STATS);

export const serverScreeningTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
const serverScreeningMaxRunTimerAtom = atom<ReturnType<typeof setTimeout> | null>(null);

/**
 * 캡처 시작부터 인코딩, 업로드까지 완료될 때까지 다음 캡처를 스킵하기 위한 플래그.
 * 이전 매치의 작업이 진행 중이면 완료될 때까지 다음 매치에서도 캡처를 시작하지 않는다.
 */
const moderationCaptureInFlightAtom = atom<number | null>(null);
const serverScreeningRunIdAtom = atom(0);
let nextCaptureOperationId = 0;

/**
 * 매치 단위 HVS 게이트 상태.
 * - passedPerMatchRoll: 매치당 확률 롤 결과 (한 번만 계산)
 * - maxRunDeadline: 캡처 허용 만료 시각 (Date.now() 기준, 매치 첫 시작 시 결정)
 * matchId 비교로 새 매치 시 자동 갱신되므로 별도 클리어 불필요.
 */
interface ServerScreeningPerMatchGate {
  matchId: string;
  passedPerMatchRoll: boolean;
  maxRunDeadline?: number;
}

const serverScreeningPerMatchGateAtom = atom<ServerScreeningPerMatchGate | null>(null);

interface ServerScreeningCooldown {
  matchId: string;
  cooldownUntilMs: number;
}

const serverScreeningCooldownAtom = atom<ServerScreeningCooldown | null>(null);

export const applyServerScreeningCooldownAtom = atom(
  null,
  (get, set, payload: { matchId: string; cooldownMs?: number }) => {
    const cooldownMs = payload.cooldownMs ?? 0;
    if (cooldownMs <= 0) return;

    const nextCooldownUntilMs = Date.now() + cooldownMs;
    const prev = get(serverScreeningCooldownAtom);

    if (prev?.matchId === payload.matchId) {
      set(serverScreeningCooldownAtom, {
        matchId: payload.matchId,
        cooldownUntilMs: Math.max(prev.cooldownUntilMs, nextCooldownUntilMs),
      });
      return;
    }

    set(serverScreeningCooldownAtom, {
      matchId: payload.matchId,
      cooldownUntilMs: nextCooldownUntilMs,
    });
  }
);

// --- Capture logic ---

export const stopServerScreeningAtom = atom(null, (get, set) => {
  const timerId = get(serverScreeningTimerAtom);
  if (timerId) {
    clearInterval(timerId);
    set(serverScreeningTimerAtom, null);
  }
  const maxRunTimerId = get(serverScreeningMaxRunTimerAtom);
  if (maxRunTimerId) {
    clearTimeout(maxRunTimerId);
    set(serverScreeningMaxRunTimerAtom, null);
  }
  set(serverScreeningRunIdAtom, (prev) => prev + 1);
  set(serverScreeningStatsAtom, (prev) => ({
    ...prev,
    status: 'idle',
  }));
});

export const startServerScreeningAtom = atom(
  null,
  async (get, set, peerVideoCanvas: HTMLCanvasElement) => {
    const existingTimerId = get(serverScreeningTimerAtom);
    if (existingTimerId) {
      clearInterval(existingTimerId);
      set(serverScreeningTimerAtom, null);
    }
    const existingMaxRunTimerId = get(serverScreeningMaxRunTimerAtom);
    if (existingMaxRunTimerId) {
      clearTimeout(existingMaxRunTimerId);
      set(serverScreeningMaxRunTimerAtom, null);
    }

    const screeningRunId = get(serverScreeningRunIdAtom) + 1;
    set(serverScreeningRunIdAtom, screeningRunId);

    let timerId: ReturnType<typeof setInterval> | null = null;
    let maxRunTimerId: ReturnType<typeof setTimeout> | null = null;

    const remoteConfig = get(remoteConfigAtom);
    if (!remoteConfig || !isObjectKeyTrue(remoteConfig, 'humanVisionServerScreeningEnabled')) {
      set(stopServerScreeningAtom);
      return;
    }

    const matchId = get(matchInfoAtom)?.matchId ?? '';
    const probabilityPerMatch =
      toFiniteNumber(remoteConfig.humanVisionServerScreeningProbabilityPerMatch) ?? 0;
    const gate = get(serverScreeningPerMatchGateAtom);

    if (
      gate?.matchId === matchId &&
      gate.maxRunDeadline != null &&
      Date.now() >= gate.maxRunDeadline
    ) {
      set(stopServerScreeningAtom);
      return;
    }

    let passedPerMatchRoll: boolean;
    if (gate?.matchId === matchId) {
      passedPerMatchRoll = gate.passedPerMatchRoll;
    } else {
      passedPerMatchRoll = Math.random() <= probabilityPerMatch;
      set(serverScreeningPerMatchGateAtom, { matchId, passedPerMatchRoll });
    }

    if (!passedPerMatchRoll) {
      set(stopServerScreeningAtom);
      return;
    }

    const probabilityPerFrame =
      toFiniteNumber(remoteConfig.humanVisionServerScreeningProbabilityPerFrame) ?? 0;
    const maxRunMs = toFiniteNumber(remoteConfig.humanVisionServerScreeningMaxRunMs);
    const currentGate = get(serverScreeningPerMatchGateAtom);
    const maxRunDeadline =
      maxRunMs != null && maxRunMs > 0
        ? currentGate?.matchId === matchId && currentGate.maxRunDeadline != null
          ? currentGate.maxRunDeadline
          : Date.now() + maxRunMs
        : undefined;

    if (
      currentGate?.matchId === matchId &&
      currentGate.maxRunDeadline == null &&
      maxRunDeadline != null
    ) {
      set(serverScreeningPerMatchGateAtom, { ...currentGate, maxRunDeadline });
    }

    if (maxRunDeadline != null && Date.now() >= maxRunDeadline) {
      set(stopServerScreeningAtom);
      return;
    }

    set(serverScreeningStatsAtom, (prev) => ({
      ...prev,
      status: 'capturing',
    }));

    const intervalMs =
      toFiniteNumber(remoteConfig.humanVisionServerScreeningCaptureIntervalMs) ??
      DEFAULT_MODERATION_CAPTURE_INTERVAL_MS;
    timerId = setInterval(() => {
      if (!get(peerCameraEnabledAtom)) return;
      if (Math.random() > probabilityPerFrame) return;

      const cooldown = get(serverScreeningCooldownAtom);
      if (cooldown?.matchId === matchId && Date.now() < cooldown.cooldownUntilMs) {
        set(serverScreeningStatsAtom, (prev) => ({
          ...prev,
          skippedCount: prev.skippedCount + 1,
        }));
        return;
      }

      if (get(moderationCaptureInFlightAtom) != null) {
        set(serverScreeningStatsAtom, (prev) => ({
          ...prev,
          skippedCount: prev.skippedCount + 1,
        }));
        return;
      }

      const captureOperationId = ++nextCaptureOperationId;
      set(moderationCaptureInFlightAtom, captureOperationId);

      const endMark = mark('canvas.encode.serverScreening');
      void encodeCanvasToPng(peerVideoCanvas, { priority: 'best-effort' })
        .then(({ blob, time }) => {
          endMark();
          if (get(serverScreeningRunIdAtom) !== screeningRunId) {
            return;
          }

          set(serverScreeningStatsAtom, (prev) => ({
            ...prev,
            capturedCount: prev.capturedCount + 1,
            lastCaptureSize: blob.size,
            lastCaptureTimeMs: time,
          }));

          return uploadContentManager(blob, { skipResize: true })
            .then((fileInfo) => {
              if (get(serverScreeningRunIdAtom) !== screeningRunId) {
                return;
              }

              return uploadServerScreeningContents({
                screen: Screen.MATCH,
                matchId: matchId || undefined,
                target: ScreeningTarget.PEER,
                contentType: ScreeningContentType.IMAGE,
                fileInfo,
              });
            })
            .then(() => {
              if (get(serverScreeningRunIdAtom) !== screeningRunId) {
                return;
              }

              set(serverScreeningStatsAtom, (prev) => ({
                ...prev,
                uploadedCount: prev.uploadedCount + 1,
              }));
            });
        })
        .catch((error) => {
          if (get(serverScreeningRunIdAtom) !== screeningRunId) {
            return;
          }

          if (isCanvasBlobEncodeDroppedError(error)) {
            set(serverScreeningStatsAtom, (prev) => ({
              ...prev,
              skippedCount: prev.skippedCount + 1,
            }));
            return;
          }

          Sentry.captureMessage('Failed to process server screening contents', {
            extra: { error, matchId },
          });
          set(serverScreeningStatsAtom, (prev) => ({
            ...prev,
            errorCount: prev.errorCount + 1,
          }));
        })
        .finally(() => {
          if (get(moderationCaptureInFlightAtom) === captureOperationId) {
            set(moderationCaptureInFlightAtom, null);
          }
        });
    }, intervalMs);

    set(serverScreeningTimerAtom, timerId);

    if (maxRunDeadline != null) {
      const remaining = maxRunDeadline - Date.now();
      if (remaining <= 0) {
        set(stopServerScreeningAtom);
        return;
      }

      maxRunTimerId = setTimeout(() => {
        set(stopServerScreeningAtom);
      }, remaining);
      set(serverScreeningMaxRunTimerAtom, maxRunTimerId);
    }
  }
);
